/**
 * @typedef {import('./types').Match} Match
 * @typedef {import('./types').Venue} Venue
 */

/**
 * Resolve the venue for a match against a list of known venues.
 *
 * Notes:
 * - Returns an array (rather than a single object) for backwards compatibility
 *   with existing consumers that iterate over the result.
 * - Falls back to `'Qatar'` for `city` if the venue lookup is missing one —
 *   a project-specific default carried over from the previous implementation.
 *
 * Performance (issue #29):
 * The lookup used to be a linear `.find()` over `venues` on every call,
 * which adds up quickly when called per match in markup (32 knockout
 * matches × every reactive render). We now build a `Map<id, Venue>`
 * lazily, keyed on the `venues` array reference via a `WeakMap`, so the
 * lookup itself is O(1) and the index is shared across every caller
 * (Bracket, AccessibleBracket, MatchPage, …). The index auto-GCs when
 * the `venues` array is replaced by a fresh poller tick.
 *
 * @param {Match | undefined} match
 * @param {Venue[]} venues
 * @returns {Venue[]}
 */

/**
 * Per-`venues`-array cache. Keyed on the array reference itself so a new
 * poller tick (which produces a fresh `venues` reference) builds a new
 * index, and the old one is collected.
 *
 * @type {WeakMap<object, Map<string, Venue>>}
 */
const indexCache = new WeakMap();

const getIndex = (venues) => {
  // Primitive / non-array inputs can't be cached in a WeakMap; just bail.
  if (!Array.isArray(venues)) return null;
  const hit = indexCache.get(venues);
  if (hit) return hit;
  const map = new Map();
  for (const v of venues) {
    if (v && v.id) map.set(v.id, v);
  }
  indexCache.set(venues, map);
  return map;
};

const getVenue = (match, venues) => {
  const matchVenueId = match?.matchInfo?.venue?.id;
  if (!matchVenueId) return [];

  const index = getIndex(venues);
  if (!index) return [];
  const venue = index.get(matchVenueId);
  if (!venue) return [];

  return [{ ...venue, city: venue.city ?? 'Qatar' }];
};

export default getVenue;
